import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RefreshCw, Coins, PackageCheck, AlertCircle, TrendingUp, Download, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GHS = (n) => `GHS ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const NUM = (n) => Number(n || 0).toLocaleString();

const PIPELINE_COLORS = {
  pending: '#f59e0b',
  picking: '#8b5cf6',
  packed: '#00a89c',
  shipped: '#1680e5',
  cancelled: '#94a3b8',
};

const DAY_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'All inventory' },
  { value: 'customer', label: 'Customer only' },
  { value: 'internal', label: 'Internal only' },
];

export default function Analytics() {
  const [days, setDays] = useState('30');
  const [scope, setScope] = useState('all');
  const [reqMetric, setReqMetric] = useState('value'); // 'value' | 'quantity' | 'transactions'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { load(); }, [days]);

  const load = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/dashboard/analytics`, {
        params: { days: Number(days), top_categories: 12, top_low_stock: 20, top_requesters: 10 },
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filteredCategories = useMemo(() => {
    if (!data) return [];
    if (scope === 'all') return data.value_by_category;
    return data.value_by_category.filter((c) => c.inventory_type === scope);
  }, [data, scope]);

  const filteredLowStock = useMemo(() => {
    if (!data) return [];
    if (scope === 'all') return data.top_low_stock;
    return data.top_low_stock.filter((r) => r.inventory_type === scope);
  }, [data, scope]);

  const chartCats = filteredCategories.map((c) => ({
    name: c.category.length > 24 ? c.category.slice(0, 22) + '…' : c.category,
    value: c.value,
    count: c.count,
    type: c.inventory_type,
  }));

  const movementData = (data?.movement_trend || []).map((m) => ({
    date: m.date.slice(5),
    inbound: m.inbound,
    outbound: m.outbound,
    net: m.inbound - m.outbound,
  }));

  const pipelineData = (data?.order_pipeline || [])
    .filter((p) => p.count > 0)
    .map((p) => ({ name: p.status, value: p.count, fill: PIPELINE_COLORS[p.status] || '#94a3b8' }));

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ['section', 'key', 'value'],
      ['hero', 'total_stock_value_customer', data.hero.total_stock_value_customer],
      ['hero', 'total_stock_value_internal', data.hero.total_stock_value_internal],
      ['hero', 'total_stock_value', data.hero.total_stock_value],
      ['hero', 'total_items_on_hand', data.hero.total_items_on_hand],
      ['hero', 'total_low_stock', data.hero.total_low_stock],
      [],
      ['value_by_category', 'category', 'value_ghs', 'count', 'inventory_type'],
      ...data.value_by_category.map((c) => [c.category, c.value, c.count, c.inventory_type]),
      [],
      ['movement_trend', 'date', 'inbound', 'outbound'],
      ...data.movement_trend.map((m) => [m.date, m.inbound, m.outbound]),
      [],
      ['top_low_stock', 'sku', 'name', 'quantity', 'reorder_point', 'shortage', 'type'],
      ...data.top_low_stock.map((r) => [r.sku, r.name, r.quantity, r.reorder_point, r.shortage, r.inventory_type]),
      [],
      ['consumption_by_requester', 'requestor', 'quantity', 'value_ghs', 'transactions', 'last_activity'],
      ...(data.consumption_by_requester || []).map((r) => [r.requestor, r.quantity, r.value, r.transactions, r.last_activity || '']),
    ];
    const csv = rows.map((r) => r.map((v) => (typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? '')).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zippy-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Analytics exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const hero = data?.hero || {};
  const scopedValue =
    scope === 'customer' ? hero.total_stock_value_customer :
    scope === 'internal' ? hero.total_stock_value_internal :
    hero.total_stock_value;

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Analytics</h1>
          <p className="text-slate-600 mt-2">Deeper view of stock value, movement, and pipeline</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-40" data-testid="analytics-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-44" data-testid="analytics-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="analytics-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="analytics-export">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Hero cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm" data-testid="analytics-hero-value">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Stock value ({scope === 'all' ? 'total' : scope})
            </CardTitle>
            <div className="bg-blue-50 p-2 rounded-lg">
              <Coins className="h-5 w-5 text-[#1680e5]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{GHS(scopedValue)}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Items on hand</CardTitle>
            <div className="bg-teal-50 p-2 rounded-lg">
              <PackageCheck className="h-5 w-5 text-[#00a89c]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{NUM(hero.total_items_on_hand)}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Low-stock alerts</CardTitle>
            <div className="bg-orange-50 p-2 rounded-lg">
              <AlertCircle className="h-5 w-5 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{NUM(hero.total_low_stock)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Value by category */}
      <Card className="border-slate-200" data-testid="analytics-chart-category">
        <CardHeader>
          <CardTitle className="text-base">Stock value by category</CardTitle>
        </CardHeader>
        <CardContent className="h-96">
          {chartCats.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartCats} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#334155', fontSize: 12 }} width={160} />
                <Tooltip
                  formatter={(v, _, props) => [GHS(v), `${props?.payload?.count || 0} SKUs`]}
                  contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chartCats.map((entry, idx) => (
                    <Cell key={idx} fill={entry.type === 'internal' ? '#00a89c' : '#1680e5'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Movement trend + Order pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200" data-testid="analytics-chart-movement">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#1680e5]" />
              Stock movement — {DAY_OPTIONS.find((o) => o.value === days)?.label.toLowerCase()}
            </CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={movementData} margin={{ left: 6, right: 20, top: 6 }}>
                <defs>
                  <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00a89c" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00a89c" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="inbound" stroke="#00a89c" strokeWidth={2.5} fill="url(#gIn)" name="Inbound" />
                <Area type="monotone" dataKey="outbound" stroke="#ef4444" strokeWidth={2.5} fill="url(#gOut)" name="Outbound" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="analytics-chart-pipeline">
          <CardHeader>
            <CardTitle className="text-base">Order pipeline</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {pipelineData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-400 text-sm">No orders</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pipelineData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={95} paddingAngle={2}>
                    {pipelineData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12, textTransform: 'capitalize' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Net movement bar */}
      <Card className="border-slate-200" data-testid="analytics-chart-net">
        <CardHeader>
          <CardTitle className="text-base">Daily net change (inbound − outbound)</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={movementData} margin={{ left: 6, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0' }} />
              <Bar dataKey="net" radius={[4, 4, 0, 0]}>
                {movementData.map((d, i) => (
                  <Cell key={i} fill={d.net >= 0 ? '#00a89c' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Consumption by requester */}
      <Card className="border-slate-200" data-testid="analytics-chart-requesters">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-[#00a89c]" />
            Consumption by requester — {DAY_OPTIONS.find((o) => o.value === days)?.label.toLowerCase()}
          </CardTitle>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 text-xs">
            {[
              { key: 'value', label: 'By value' },
              { key: 'quantity', label: 'By quantity' },
              { key: 'transactions', label: 'By transactions' },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => setReqMetric(m.key)}
                className={
                  'px-3 py-1 rounded-md font-medium transition-colors ' +
                  (reqMetric === m.key
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900')
                }
                data-testid={`requester-metric-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {(data?.consumption_by_requester || []).length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
              No outbound activity in this window
            </div>
          ) : (
            <>
              <div className="h-72 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...data.consumption_by_requester].sort((a, b) => b[reqMetric] - a[reqMetric])}
                    layout="vertical"
                    margin={{ left: 20, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      tick={{ fill: '#64748b', fontSize: 11 }}
                      tickFormatter={(v) => reqMetric === 'value' ? `${Math.round(v / 1000)}k` : v}
                    />
                    <YAxis type="category" dataKey="requestor" tick={{ fill: '#334155', fontSize: 12 }} width={170} />
                    <Tooltip
                      contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0' }}
                      formatter={(v) => reqMetric === 'value' ? GHS(v) : NUM(v)}
                    />
                    <Bar dataKey={reqMetric} fill="#00a89c" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 pr-4 font-medium">Requestor</th>
                      <th className="text-right py-2 pr-4 font-medium">Quantity</th>
                      <th className="text-right py-2 pr-4 font-medium">Value (GHS)</th>
                      <th className="text-right py-2 pr-4 font-medium">Transactions</th>
                      <th className="text-right py-2 font-medium">Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data.consumption_by_requester]
                      .sort((a, b) => b[reqMetric] - a[reqMetric])
                      .map((r) => (
                        <tr key={r.requestor} className="border-b border-slate-100" data-testid={`requester-row-${r.requestor}`}>
                          <td className="py-2 pr-4 font-medium text-slate-900">{r.requestor}</td>
                          <td className="py-2 pr-4 text-right">{NUM(r.quantity)}</td>
                          <td className="py-2 pr-4 text-right">{GHS(r.value)}</td>
                          <td className="py-2 pr-4 text-right">{NUM(r.transactions)}</td>
                          <td className="py-2 text-right text-slate-500 text-xs">
                            {r.last_activity ? new Date(r.last_activity).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Low stock watchlist expanded */}
      <Card className="border-slate-200" data-testid="analytics-low-stock-table">
        <CardHeader>
          <CardTitle className="text-base">Low-stock watchlist</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLowStock.length === 0 ? (
            <p className="text-slate-500 text-sm py-6 text-center">All items above reorder point.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-4 font-medium">SKU</th>
                    <th className="text-left py-2 pr-4 font-medium">Name</th>
                    <th className="text-left py-2 pr-4 font-medium">Type</th>
                    <th className="text-right py-2 pr-4 font-medium">On hand</th>
                    <th className="text-right py-2 pr-4 font-medium">Reorder pt</th>
                    <th className="text-right py-2 font-medium text-orange-600">Shortage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLowStock.map((row) => (
                    <tr key={row.sku} className="border-b border-slate-100">
                      <td className="py-2 pr-4 font-mono text-xs">{row.sku}</td>
                      <td className="py-2 pr-4">{row.name}</td>
                      <td className="py-2 pr-4">
                        <span className={
                          'inline-block px-2 py-0.5 rounded-full text-xs font-medium ' +
                          (row.inventory_type === 'internal' ? 'bg-teal-50 text-teal-700' : 'bg-blue-50 text-blue-700')
                        }>
                          {row.inventory_type === 'internal' ? 'Internal' : 'Customer'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">{NUM(row.quantity)}</td>
                      <td className="py-2 pr-4 text-right text-slate-500">{NUM(row.reorder_point)}</td>
                      <td className="py-2 text-right font-semibold text-orange-600">−{NUM(row.shortage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
